// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Center, em, Flex, Loader, Paper, ScrollArea, SegmentedControl } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { MedplumClient } from '@medplum/core';
import type { Reference, ResourceType, Task } from '@medplum/fhirtypes';
import { ResourceTimeline, useMedplum, useResource } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { isDmeSubmissionTask } from '../../utils/dme-task';
import { showErrorNotification } from '../../utils/notifications';
import { DmeTaskDetail } from './DmeTaskDetail';
import classes from './TaskBoard.module.css';
import { TaskInputNote } from './TaskInputNote';
import { TaskProperties } from './TaskProperties';

interface TaskDetailPanelProps {
  task: Task | Reference<Task>;
  onTaskChange?: (task: Task) => void;
  onDeleteTask?: (task: Task) => void;
}

export function TaskDetailPanel(props: TaskDetailPanelProps): JSX.Element | null {
  const { task: taskProp, onTaskChange, onDeleteTask } = props;
  const medplum = useMedplum();
  const resolvedTask = useResource(taskProp);
  const [task, setTask] = useState<Task | undefined>(resolvedTask);
  const [activeTab, setActiveTab] = useState<string>('properties');

  useEffect(() => {
    if (resolvedTask) {
      setTask(resolvedTask);
    }
  }, [resolvedTask]);

  const isMobile = useMediaQuery(`(max-width: ${em(992)})`, false, { getInitialValueInEffect: false });

  if (!task) {
    return (
      <Center h="100%" style={{ flex: 1 }} data-testid="task-loading">
        <Loader size="md" />
      </Center>
    );
  }

  const handleTaskChange = async (updatedTask: Task): Promise<void> => {
    await medplum.updateResource(updatedTask);
    setTask(updatedTask);
    onTaskChange?.(updatedTask);
  };

  const handleDeleteTask = async (deletedTask: Task): Promise<void> => {
    try {
      await medplum.deleteResource('Task', deletedTask.id as string);
      onDeleteTask?.(deletedTask);
    } catch (error) {
      showErrorNotification(error);
    }
  };

  const handleTabChange = (value: string): void => {
    setActiveTab(value);
  };

  // The DME flow persists its own writes; this only syncs local + parent state.
  const handleDmeTaskUpdated = (updatedTask: Task): void => {
    setTask(updatedTask);
    onTaskChange?.(updatedTask);
  };

  const getTabData = (): { label: string; value: string }[] => {
    return [
      { label: 'Properties', value: 'properties' },
      { label: 'Activity Log', value: 'activity-log' },
    ];
  };

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      style={{ flex: 1, overflow: isMobile ? 'auto' : 'hidden' }}
      h="100%"
    >
      <Box style={{ flex: 1, minHeight: isMobile ? 'auto' : '100%' }} className={classes.borderRight}>
        {isDmeSubmissionTask(task) ? (
          <DmeTaskDetail task={task} onTaskUpdated={handleDmeTaskUpdated} />
        ) : (
          <TaskInputNote
            task={task}
            onTaskChange={handleTaskChange}
            onDeleteTask={onDeleteTask ? handleDeleteTask : undefined}
          />
        )}
      </Box>

      <Box w={{ base: '100%', md: '400px' }} style={{ flexShrink: 0 }}>
        <Paper style={{ overflow: 'hidden' }}>
          <Box px="md" pb="md" pt="md">
            <SegmentedControl
              value={activeTab}
              onChange={handleTabChange}
              data={getTabData()}
              fullWidth
              radius="md"
              color="gray"
              size="sm"
              className={classes.segmentedControl}
            />
          </Box>

          <Box>
            {activeTab === 'properties' && (
              <ScrollArea h={isMobile ? 'auto' : 'calc(100vh - 120px)'}>
                <TaskProperties key={task.id} p="md" task={task} onTaskChange={handleTaskChange} />
              </ScrollArea>
            )}
            {activeTab === 'activity-log' && (
              <ScrollArea h={isMobile ? 'auto' : 'calc(100vh - 120px)'}>
                <ResourceTimeline
                  value={task}
                  loadTimelineResources={async (medplum: MedplumClient, _resourceType: ResourceType, id: string) => {
                    return Promise.allSettled([medplum.readHistory('Task', id)]);
                  }}
                />
              </ScrollArea>
            )}
          </Box>
        </Paper>
      </Box>
    </Flex>
  );
}
